import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { dailyLogDetailToFormSchema } from "@features/daily-log-detail/model/schema";
import { latestDailyLogQueryOptions } from "@features/daily-log-detail/model/useDailyLogDetailQuery";
import type { DailyLogFormValues } from "@features/write-daily-log/model/schema";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef } from "react";
import { draftDetailContentToFormSchema } from "./schema";
import { draftDetailQueryOptions } from "./useDraftListQuery";
import { useDraftStore } from "./useDraftStore";

/**
 * 폼 초기값이 어디서 왔는지.
 * - `local` / `server`: 저장된 드래프트를 복구한 경우
 * - `latest_log`: 드래프트가 없어 직전 공사일지로 프리필한 경우
 * - `none`: 빈 폼으로 시작
 */
export type DraftRestoreSource = "local" | "server" | "latest_log" | "none";

interface ResolvedDraftResult {
  defaultValues: Partial<DailyLogFormValues> | undefined;
  resolvedVersion: number;
  /** 서버/최신일지 조회가 끝나기 전까지 true (로컬 초안이 있어도 동일) */
  isInitialSyncPending: boolean;
}

interface UseSyncDraftToFormOptions {
  serverFallback?: boolean;
}

/**
 * 드래프트 기본값을 우선-로컬, 서버-폴백 방식으로 결정합니다.
 *
 * 1. 로컬 O, 서버 X → 로컬 사용
 * 2. 로컬 X, 서버 O → 서버 데이터를 변환 후 로컬에 seed
 * 3. 둘 다 O → version 비교, 높은 쪽 사용 (서버가 높으면 로컬 갱신)
 * 4. 둘 다 X → 최신 공사일지 데이터로 폼 초기화
 * 5. 그외 → undefined (폼에서 자체 기본값 사용)
 */
export function useSyncDraftToForm(
  projectId: number,
  draftId: string,
  { serverFallback = true }: UseSyncDraftToFormOptions = {},
): ResolvedDraftResult {
  const seeded = useRef(false);

  const { draft: localDraft, upsert } = useDraftStore(draftId, projectId);

  const serverQuery = useQuery({
    ...draftDetailQueryOptions(projectId, draftId),
    enabled: serverFallback,
    gcTime: 0,
  });

  const latestLogQuery = useQuery({
    ...latestDailyLogQueryOptions(projectId),
    enabled: !serverFallback,
    gcTime: 0,
  });

  const serverFormValues = useMemo(() => {
    if (!serverQuery.data?.content) return undefined;

    const parsed = draftDetailContentToFormSchema.safeParse(serverQuery.data.content);

    return parsed.success ? parsed.data : undefined;
  }, [serverQuery.data]);

  const latestLogFormValues = useMemo(() => {
    if (!latestLogQuery.data) return undefined;
    const parsed = dailyLogDetailToFormSchema.safeParse(latestLogQuery.data);
    if (!parsed.success) return undefined;
    return {
      ...parsed.data,
      weather: "",
      temperature: 0,
      precipitation: 0,
      description: "",
      image_urls: [],
      date: dayjs().format("YYYY-MM-DD"),
    };
  }, [latestLogQuery.data]);

  const localVersion = localDraft?.version ?? 0;
  const serverVersion = serverQuery.data?.version ?? 0;
  const serverIsNewer = serverQuery.data && serverFormValues && serverVersion > localVersion;

  useEffect(() => {
    if (seeded.current || !serverQuery.data || !serverFormValues) return;
    if (localDraft && !serverIsNewer) return;
    seeded.current = true;

    upsert({
      draft_id: serverQuery.data.draft_id,
      version: serverQuery.data.version,
      content: serverFormValues,
      updated_at: serverQuery.data.updated_at,
    });
  }, [localDraft, serverQuery.data, serverFormValues, serverIsNewer, upsert]);

  const isInitialSyncPending =
    (serverFallback && serverQuery.isLoading) || (!serverFallback && latestLogQuery.isLoading);

  let source: DraftRestoreSource = "none";
  let defaultValues: Partial<DailyLogFormValues> | undefined;
  let resolvedVersion = 0;

  if (serverIsNewer) {
    source = "server";
    defaultValues = serverFormValues;
    resolvedVersion = serverVersion;
  } else if (localDraft?.content) {
    source = "local";
    defaultValues = localDraft.content as Partial<DailyLogFormValues>;
    resolvedVersion = localVersion;
  } else if (serverFormValues) {
    source = "server";
    defaultValues = serverFormValues;
    resolvedVersion = serverVersion;
  } else if (latestLogFormValues) {
    source = "latest_log";
    defaultValues = latestLogFormValues;
  }

  // 드래프트가 실제로 유저를 구하고 있는지 보려면 "복구가 일어났다"는 사실 자체가 필요하다.
  //
  // 판정은 **모든 소스가 확정된 첫 시점에 한 번만** 내린다. 이 시점을 놓치면 두 방향으로 틀린다.
  // - 너무 이르면: IndexedDB 조회가 끝나기 전(`localDraft === undefined`)이라 로컬 드래프트가
  //   있는데도 `latest_log` 로 잘못 기록된다. 최신 공사일지 쿼리가 캐시에 있으면 실제로 그렇게 된다.
  // - 너무 늦으면(= 확정 후에도 계속 관찰하면): 자동 저장이 드래프트를 "새로" 만든 순간
  //   source 가 `local` 로 뒤집혀, 방금 만든 드래프트가 복구된 것으로 집계된다.
  // 그래서 확정 시점에 source 가 `none` 이더라도 ref 를 닫아 이후 변화를 무시한다.
  const isLocalDraftSettled = localDraft !== undefined;
  const restoreReportedRef = useRef(false);
  useEffect(() => {
    if (restoreReportedRef.current || isInitialSyncPending || !isLocalDraftSettled) return;
    restoreReportedRef.current = true;

    if (source === "none") return;

    EventLogger.track(CLIENT_EVENT.DRAFT_RESTORED, {
      // 다른 이벤트와 조인 가능하도록 project_id 는 항상 문자열로 보낸다.
      project_id: String(projectId),
      draft_id: draftId,
      source,
      resolved_version: resolvedVersion,
      local_version: localVersion,
      server_version: serverVersion,
      is_server_newer: Boolean(serverIsNewer),
    });
  }, [
    isInitialSyncPending,
    isLocalDraftSettled,
    source,
    resolvedVersion,
    localVersion,
    serverVersion,
    serverIsNewer,
    projectId,
    draftId,
  ]);

  return { defaultValues, resolvedVersion, isInitialSyncPending };
}
